import { z } from "zod";
import {
  accessFields,
  actorSchema,
  idSchema,
  instantSchema,
  moneySchema,
  outcomeClassSchema,
  sourceDateSchema,
  stageSchema,
} from "@amber/contracts";
import { countSchema, httpUrlSchema, platformSchema } from "./shared.js";

export const STAGE_STATES = [
  "queued",
  "running",
  "retry_scheduled",
  "waiting",
  "failed",
  "done",
  "not_applicable",
] as const;
export const stageStateSchema = z.enum(STAGE_STATES);

export const WAITING_REASONS = [
  "access_needed",
  "rate_limited",
  "budget",
  "awaiting_publication",
  "scheduled",
  "paused_global",
  "paused_stage",
  "provider_outcome_unknown",
  "route_quarantined",
] as const;
export const waitingReasonSchema = z.enum(WAITING_REASONS);

export const FAILURE_REASONS = ["attempts_exhausted", "unprocessable_input", "stopped_by_operator"] as const;
export const failureReasonSchema = z.enum(FAILURE_REASONS);

export const EXCLUSION_KINDS = ["not_relevant", "duplicate_handled_elsewhere", "withdrawn"] as const;
export const exclusionKindSchema = z.enum(EXCLUSION_KINDS);

export const ITEM_KINDS = [
  "video",
  "short",
  "livestream_replay",
  "episode",
  "post",
  "thread",
  "article",
  "page",
  "document",
  "official_record",
] as const;
export const itemKindSchema = z.enum(ITEM_KINDS);
export type ItemKind = z.infer<typeof itemKindSchema>;

export const DISCOVERY_METHODS = ["scan", "search", "editor_added", "referenced_original"] as const;
export const discoveryMethodSchema = z.enum(DISCOVERY_METHODS);

export const SELECTION_REASONS = [
  "own_source",
  "shared_source_name_match",
  "shared_source_unmatched",
  "shared_source_expiring",
  "appearance_search",
  "recheck",
  "editor_added",
  "referenced_original",
] as const;
export const selectionReasonSchema = z.enum(SELECTION_REASONS);

export const ITEM_REFERENCE_KINDS = [
  "quotes",
  "quoted_by",
  "full_edition",
  "preview",
  "reports_on",
  "reported_in",
] as const;
export const itemReferenceSchema = z.strictObject({
  kind: z.enum(ITEM_REFERENCE_KINDS),
  item_id: idSchema("itm"),
  post_id: z.string().min(1),
  url: httpUrlSchema,
});
export type ItemReference = z.infer<typeof itemReferenceSchema>;

/** Machine keys for resume conditions that code resumes on; the text beside a key is for people only (DI-77). */
export const RESUME_KEYS = ["route_release", "processing_route_release"] as const;

export const stageNextStepSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("automatic_retry"), at: instantSchema }),
  z.strictObject({
    kind: z.literal("resume_condition"),
    key: z.enum(RESUME_KEYS).optional(),
    text: z.string().min(1),
  }),
  z.strictObject({ kind: z.literal("needs_person"), text: z.string().min(1) }),
]);

export const stageProgressSchema = z.strictObject({
  done: countSchema,
  total: countSchema.nullable(),
  unit: z.string().min(1),
});

const stageFields = {
  attempts_used: countSchema,
  attempts_allowed: z.number().int().positive(),
  entered_at: instantSchema.nullable(),
  finished_at: instantSchema.nullable(),
  cost: moneySchema.nullable(),
  progress: stageProgressSchema.nullable(),
  route: idSchema("rte").optional(),
  manual_attempts_used: z.number().int().positive().optional(),
};

export const itemStageSchema = z.discriminatedUnion("state", [
  z.strictObject({
    state: z.enum(["queued", "running", "done", "not_applicable"]),
    reason: z.null(),
    next_step: z.null(),
    ...stageFields,
  }),
  z.strictObject({
    state: z.literal("retry_scheduled"),
    reason: outcomeClassSchema,
    next_step: stageNextStepSchema,
    ...stageFields,
  }),
  z.strictObject({
    state: z.literal("waiting"),
    reason: waitingReasonSchema,
    next_step: stageNextStepSchema,
    ...stageFields,
  }),
  z.strictObject({
    state: z.literal("failed"),
    reason: failureReasonSchema,
    next_step: stageNextStepSchema,
    ...stageFields,
  }),
]);
export type ItemStage = z.infer<typeof itemStageSchema>;

export type ItemStages = z.infer<typeof itemStagesSchema>;
export type Exclusion = z.infer<typeof exclusionSchema>;

export const itemStagesSchema = z.strictObject({
  discover: itemStageSchema,
  download: itemStageSchema,
  archive: itemStageSchema,
  transcribe: itemStageSchema,
  speakers: itemStageSchema,
  ready: itemStageSchema,
});

export const discoverySchema = z.strictObject({
  method: discoveryMethodSchema,
  at: instantSchema,
  scan_id: idSchema("scn").nullable(),
  by: actorSchema.nullable(),
  note: z.string().min(1).optional(),
});

const exclusionFields = {
  kind: exclusionKindSchema,
  by: actorSchema,
  reason: z.string().min(1),
  at: instantSchema,
};

export const exclusionSchema = z.strictObject(exclusionFields);

export const pastExclusionSchema = z.strictObject({
  ...exclusionFields,
  cleared_by: actorSchema,
  cleared_at: instantSchema,
  cleared_reason: z.string().min(1).nullable(),
});

export const editorAdditionSchema = z.strictObject({
  by: actorSchema,
  reason: z.string().min(1),
  date: sourceDateSchema.nullable(),
  participants: z.array(idSchema("prf")),
  audit_id: idSchema("aud"),
});

export const itemRecordSchema = z
  .strictObject({
    id: idSchema("itm"),
    source_id: idSchema("src").nullable(),
    platform: platformSchema,
    kind: itemKindSchema,
    title: z.string().min(1),
    original_url: httpUrlSchema,
    canonical_url: httpUrlSchema,
    discoveries: z.array(discoverySchema).min(1),
    profile_candidates: z.array(idSchema("prf")),
    selection_reason: selectionReasonSchema,
    exclusion: exclusionSchema.nullable(),
    stages: itemStagesSchema,
    current_version_id: idSchema("ver").nullable(),
    ...accessFields,
    revision: z.number().int().positive(),
    added: editorAdditionSchema.optional(),
    exclusion_history: z.array(pastExclusionSchema),
  })
  .refine((i) => i.stages.discover.state === "done", {
    message: "discover is done as soon as the item exists",
    path: ["stages", "discover", "state"],
  })
  .refine((i) => (i.selection_reason === "editor_added") === (i.added !== undefined), {
    message: "an Editor-added item, and only such an item, records the addition",
    path: ["added"],
  });
export type ItemRecord = z.infer<typeof itemRecordSchema>;

export const ATTEMPT_KINDS = ["automatic", "manual"] as const;
export const attemptKindSchema = z.enum(ATTEMPT_KINDS);

export const ATTEMPT_STATES = ["running", "succeeded", "failed", "unknown_outcome"] as const;
export const attemptStateSchema = z.enum(ATTEMPT_STATES);

export const attemptSubjectSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("item_stage"), item_id: idSchema("itm"), stage: stageSchema }),
  z.strictObject({ kind: z.literal("scan"), scan_id: idSchema("scn") }),
]);

export const proxyRotationSchema = z.strictObject({
  proxy_label: z.string().min(1),
  outcome: outcomeClassSchema,
  at: instantSchema,
});

export const attemptRecordSchema = z
  .strictObject({
    id: idSchema("att"),
    subject: attemptSubjectSchema,
    kind: attemptKindSchema,
    number: z.number().int().positive(),
    of: z.number().int().positive(),
    route_id: idSchema("rte").nullable(),
    state: attemptStateSchema,
    outcome: outcomeClassSchema.nullable(),
    requested_by: actorSchema.optional(),
    bulk_job_id: idSchema("job").optional(),
    resolved_from_unknown_outcome: z
      .strictObject({ at: instantSchema, provider_job_id: z.string().min(1), method: z.literal("lookup_by_job_id") })
      .optional(),
    proxy_rotations: z.array(proxyRotationSchema).max(3),
    bytes: countSchema,
    proxy_bytes: countSchema,
    duration_ms: countSchema.nullable(),
    cost_entry_id: idSchema("cst").nullable(),
    engine_run: z.strictObject({ workflow_id: z.string().min(1), run_id: z.string().min(1) }),
    started_at: instantSchema,
    finished_at: instantSchema.nullable(),
    evidence_artifact_ids: z.array(idSchema("art")),
  })
  .refine((a) => a.number <= a.of, { message: "attempt number exceeds its budget", path: ["number"] })
  .refine((a) => (a.state === "succeeded") === (a.outcome === "success"), {
    message: "only a succeeded attempt has the outcome success",
    path: ["outcome"],
  })
  .refine((a) => (a.state === "running" || a.state === "unknown_outcome") === (a.outcome === null), {
    message: "finished attempts, and only they, have an outcome",
    path: ["outcome"],
  })
  .refine((a) => (a.state === "running") === (a.finished_at === null), {
    message: "a running attempt, and only a running attempt, has no finish time",
    path: ["finished_at"],
  })
  .refine((a) => (a.kind === "manual") === (a.requested_by !== undefined), {
    message: "a manual attempt, and only a manual attempt, names who requested it",
    path: ["requested_by"],
  });
export type AttemptRecord = z.infer<typeof attemptRecordSchema>;
